import { type ReactNode } from "react";

import { Plus } from "@/shared/foundation/icons";
import { ButtonLink, Card, Spinner } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";

import { useAgentEligibleSkills } from "../../AgentForm/skills/useAgentEligibleSkills";
import { type SkillSelection } from "../../Agents/buildRequestTemplate";

import { SectionEditLink } from "./SectionEditLink";
import styles from "./sections.module.css";

export interface SkillsSectionProps {
  agentId: string;
  /** The skills the agent is saved with, in its own order. */
  skills: SkillSelection[];
}

/**
 * The org-shared skills the agent's AI may invoke, each with the version it is
 * pinned to. The names come from the org's skills, so a skill that is gone or
 * no longer shared shows by its id. With none attached, the card's one way in
 * is the call to attach one; the count and Edit link wait until there is
 * something to count or edit.
 */
export function SkillsSection({ agentId, skills }: SkillsSectionProps) {
  const { query, skills: eligible } = useAgentEligibleSkills();
  const byId = new Map(eligible.map((entry) => [entry.skill.skill_id, entry]));

  let body: ReactNode;
  if (skills.length === 0) {
    body = (
      <div
        className={styles.emptyState}
        data-testid="agent-detail-skills-empty"
      >
        <div className={styles.emptyText}>
          <p className={styles.emptyTitle}>No skills attached</p>
          <p className={styles.muted}>
            It works from the model alone. Attach a skill when a goal needs
            something the model can&rsquo;t do by itself, like scoring signals
            against a baseline or comparing two runs.
          </p>
        </div>
        <ButtonLink
          variant="secondary"
          icon={<Plus aria-hidden />}
          href={Goto.createEditAgentRoute(agentId, "skills")}
          data-testid="agent-detail-skills-add"
        >
          Add a skill
        </ButtonLink>
      </div>
    );
  } else if (query.isError) {
    body = (
      <p className={styles.error} data-testid="agent-detail-skills-error">
        Failed to load skills. {query.error.message}
      </p>
    );
  } else if (query.isPending) {
    body = (
      <p className={styles.loading} data-testid="agent-detail-skills-loading">
        <Spinner aria-hidden />
        Loading skills&hellip;
      </p>
    );
  } else {
    body = (
      <ul className={styles.rows}>
        {skills.map((selection) => {
          const summary = byId.get(selection.skill_id);
          return (
            <li
              key={selection.skill_id}
              className={styles.row}
              data-testid="agent-detail-skill"
              data-skill-id={selection.skill_id}
              data-resolved={summary !== undefined}
            >
              <div className={styles.rowText}>
                <span className={styles.rowTitle}>
                  {summary?.skill.name ?? selection.skill_id}
                </span>
                <p className={styles.rowSummary}>
                  {summary === undefined
                    ? "Not an org-shared skill this agent can use."
                    : summary.latest_version?.description}
                </p>
              </div>
              <span className={styles.rowTrailing}>
                {selection.version === null
                  ? "Latest"
                  : `v${selection.version}`}
              </span>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <Card.Root
      size="section"
      data-testid="agent-detail-skills"
      data-count={skills.length}
    >
      <Card.Header>
        <Card.Title>Skills</Card.Title>
        {skills.length > 0 && (
          <Card.Actions>
            <SectionEditLink
              agentId={agentId}
              section="skills"
              label="skills"
            />
          </Card.Actions>
        )}
      </Card.Header>
      <Card.Body>{body}</Card.Body>
    </Card.Root>
  );
}
